import { useState, Fragment, useEffect } from "react";
import {
  Autocomplete,
  Button,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import modalities from "constants/modalities";
import { useIntl } from "react-intl";

const countries = require("../../../constants/ISO3166-1.alpha2.json");

const filter = createFilterOptions();

const CategoryAutocomplete = ({
  categories = [],
  selectedCategory = null,
  disabled = false,
  multiple = false,
  customCategoryAllowed = true,
  officialCategoryRequired = false,
  onCategorySelected = (category) => console.log(category),
}) => {
  const intl = useIntl();
  const [value, setValue] = useState(selectedCategory);
  const [open, toggleOpen] = useState(false);
  const [dialogValue, setDialogValue] = useState({
    name: "",
    modality: modalities[0].slug,
  });

  const { officialCategories, nonOfficialCategories } = categories.reduce(
    (acc, category) => {
      const countryName = countries[category.country] || category.country;
      const groupLabel = category.is_official
        ? `${category.modality === "male" ? "Masculino" : "Femenino"} - ${category.ladder?.name || category.official_group || countryName}`
        : "Tus categorías";

      const formattedCategory = {
        ...category,
        group: groupLabel,
        index: category.is_official ? category.official_index : Infinity,
      };

      category.is_official
        ? acc.officialCategories.push(formattedCategory)
        : acc.nonOfficialCategories.push(formattedCategory);
      return acc;
    },
    { officialCategories: [], nonOfficialCategories: [] },
  );

  officialCategories.sort((a, b) => a.group.localeCompare(b.group) || a.index - b.index);
  const options = [...nonOfficialCategories, ...officialCategories];

  useEffect(() => {
    setValue(selectedCategory);
  }, [selectedCategory]);

  const handleClose = () => {
    setDialogValue({ name: "", modality: modalities[0].slug });
    toggleOpen(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const newCategory = {
      name: dialogValue.name,
      modality: dialogValue.modality,
    };
    if (multiple) {
      const newValues = [...(value || []), newCategory];
      setValue(newValues);
      onCategorySelected(newValues);
    } else {
      setValue(newCategory);
      onCategorySelected(newCategory);
    }
    handleClose();
  };

  return (
    <Fragment>
      <Autocomplete
        multiple={multiple}
        freeSolo={customCategoryAllowed}
        selectOnFocus
        clearOnBlur
        value={value}
        options={options}
        groupBy={(option) => option.group}
        getOptionDisabled={(option) => option.selectable === false}
        disabled={disabled}
        getOptionLabel={(option) => {
          if (typeof option === "string") return option;
          if (option.inputValue) return option.inputValue;
          return option.name;
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          if (customCategoryAllowed && params.inputValue !== "") {
            filtered.push({
              inputValue: params.inputValue,
              name: intl.formatMessage({ id: "category.addNamed" }, { name: params.inputValue }),
            });
          }
          return filtered;
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <li key={key} {...optionProps} style={{ opacity: option.selectable === false ? 0.55 : 1 }}>
              {option.name}
              {option.selectable === false ? ` — ${option.unavailable_reason || "No disponible"}` : ""}
            </li>
          );
        }}
        onChange={(event, newValue) => {
          if (newValue && !multiple && newValue.selectable === false) return;
          if (multiple) {
            const lastValue = newValue?.[newValue.length - 1];
            if (typeof lastValue === "string" || lastValue?.inputValue) {
              setTimeout(() => {
                toggleOpen(true);
                setDialogValue({ ...dialogValue, name: lastValue?.inputValue ?? lastValue });
              });
            } else {
              setValue(newValue);
              onCategorySelected(newValue);
            }
          } else {
            if (typeof newValue === "string" || newValue?.inputValue) {
              setTimeout(() => {
                toggleOpen(true);
                setDialogValue({ ...dialogValue, name: newValue?.inputValue ?? newValue });
              });
            } else {
              setValue(newValue);
              onCategorySelected(newValue);
            }
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            required
            inputProps={{
              ...params.inputProps,
              required: value?.length === 0,
            }}
            label={intl.formatMessage({ id: "Categoría" })}
          />
        )}
        fullWidth
      />

      <Dialog open={open} onClose={handleClose}>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Agregar una categoría nueva</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              id="name"
              value={dialogValue.name}
              onChange={(event) => setDialogValue({ ...dialogValue, name: event.target.value })}
              label="Categoría"
            />
            <FormControl margin="dense">
              <InputLabel id="modalityLabel">{intl.formatMessage({ id: "Modalidad" })}</InputLabel>
              <Select
                id="modality"
                label="Modalidad"
                labelId="modalityLabel"
                value={dialogValue.modality}
                onChange={(event) => setDialogValue({ ...dialogValue, modality: event.target.value })}
              >
                {modalities.map((modality) => (
                  <MenuItem key={modality.slug} value={modality.slug}>
                    {modality.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{intl.formatMessage({ id: "Cancelar" })}</Button>
            <Button type="submit">{intl.formatMessage({ id: "Agregar" })}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Fragment>
  );
};

export default CategoryAutocomplete;
